import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import LoadingOverlay from '../../components/ui/LoadingOverlay';
import FileUploadZone from './components/FileUploadZone';
import ImportPreview from './components/ImportPreview';
import ImportResults from './components/ImportResults';
import ImportInstructions from './components/ImportInstructions';
import Icon from '../../components/AppIcon';

const MemberImport = () => {
  const [currentStep, setCurrentStep] = useState('upload'); // 'upload', 'preview', 'processing', 'results'
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importResults, setImportResults] = useState(null);

  // Mock data for preview
  const mockPreviewData = [
    {
      id: 1,
      fullName: "João Silva Santos",
      cpf: "123.456.789-00",
      voterId: "1234567890123",
      email: "joao.santos@email.com",
      phone: "(11) 99999-9999",
      dateOfBirth: "1985-03-15",
      address: "Rua das Flores, 123",
      city: "São Paulo",
      state: "SP",
      zipCode: "01234-567",
      isValid: true,
      isDuplicate: false,
      errors: []
    },
    {
      id: 2,
      fullName: "Maria Oliveira Costa",
      cpf: "987.654.321-00",
      voterId: "9876543210987",
      email: "maria.costa@email.com",
      phone: "(21) 88888-8888",
      dateOfBirth: "1990-07-22",
      address: "Av. Copacabana, 456",
      city: "Rio de Janeiro",
      state: "RJ",
      zipCode: "22070-001",
      isValid: true,
      isDuplicate: false,
      errors: []
    },
    {
      id: 3,
      fullName: "Carlos Pereira Lima",
      cpf: "456.789.123-00",
      voterId: "4567891230456",
      email: "invalid-email",
      phone: "(31) 77777-7777",
      dateOfBirth: "1978-11-08",
      address: "Rua Minas, 789",
      city: "Belo Horizonte",
      state: "MG",
      zipCode: "30112-000",
      isValid: false,
      isDuplicate: false,
      errors: ["Invalid email format"]
    },
    {
      id: 4,
      fullName: "Ana Paula Ferreira",
      cpf: "123.456.789-00", // Duplicate CPF
      voterId: "1111222233334",
      email: "ana.ferreira@email.com",
      phone: "(85) 66666-6666",
      dateOfBirth: "1992-05-18",
      address: "Rua Ceará, 321",
      city: "Fortaleza",
      state: "CE",
      zipCode: "60000-000",
      isValid: true,
      isDuplicate: true,
      errors: []
    },
    {
      id: 5,
      fullName: "Roberto Santos Oliveira",
      cpf: "789.123.456-00",
      voterId: "7891234560789",
      email: "roberto.oliveira@email.com",
      phone: "(11) 55555-5555",
      dateOfBirth: "1980-12-03",
      address: "Av. Paulista, 1000",
      city: "São Paulo",
      state: "SP",
      zipCode: "01310-100",
      isValid: true,
      isDuplicate: false,
      errors: []
    }
  ];

  const handleFileSelect = async (file) => {
    setSelectedFile(file);
    setIsUploading(true);
    setUploadProgress(0);

    // Simulate file upload progress
    const uploadInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(uploadInterval);
          setIsUploading(false);
          // Simulate file processing and preview generation
          setTimeout(() => {
            setPreviewData(mockPreviewData);
            setCurrentStep('preview');
          }, 500);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleConfirmImport = async () => {
    setIsProcessing(true);
    
    // Simulate import processing
    setTimeout(() => {
      const validRecords = previewData?.filter(record => record?.isValid && !record?.isDuplicate);
      const failedRecords = previewData?.filter(record => !record?.isValid);
      const duplicateRecords = previewData?.filter(record => record?.isDuplicate);

      const results = {
        totalProcessed: previewData?.length,
        successfulImports: validRecords?.length,
        failedImports: failedRecords?.length,
        duplicatesSkipped: duplicateRecords?.length,
        processingTime: 2.3,
        errors: failedRecords?.map((record, index) => ({
          row: index + 2, // +2 because row 1 is header and array is 0-indexed
          message: record?.errors?.join(', '),
          data: {
            name: record?.fullName,
            cpf: record?.cpf,
            email: record?.email
          }
        }))
      };

      setImportResults(results);
      setIsProcessing(false);
      setCurrentStep('results');
    }, 3000);
  };

  const handleCancelPreview = () => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setPreviewData([]);
  };

  const handleStartNewImport = () => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setPreviewData([]);
    setImportResults(null);
    setUploadProgress(0);
  };

  const getStepIndicator = () => {
    const steps = [
      { id: 'upload', label: 'Enviar arquivo', icon: 'Upload' },
      { id: 'preview', label: 'Pré-visualizar dados', icon: 'Eye' },
      { id: 'processing', label: 'Processando', icon: 'Cog' },
      { id: 'results', label: 'Resultados', icon: 'CheckCircle' }
    ];

    const currentStepIndex = steps?.findIndex(step => step?.id === currentStep);

    return (
      <div className="flex items-center justify-center mb-8">
        {steps?.map((step, index) => (
          <React.Fragment key={step?.id}>
            <div className={`flex items-center ${
              index <= currentStepIndex ? 'text-primary' : 'text-muted-foreground'
            }`}>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                index <= currentStepIndex 
                  ? 'border-primary bg-primary text-primary-foreground' 
                  : 'border-muted-foreground'
              }`}>
                {index < currentStepIndex ? (
                  <Icon name="Check" size={16} />
                ) : (
                  <Icon name={step?.icon} size={16} />
                )}
              </div>
              <span className="ml-2 text-sm font-medium hidden sm:block">{step?.label}</span>
            </div>
            {index < steps?.length - 1 && (
              <div className={`w-12 h-0.5 mx-4 ${
                index < currentStepIndex ? 'bg-primary' : 'bg-muted-foreground'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <div className="space-y-8">
            <FileUploadZone 
              onFileSelect={handleFileSelect}
              isUploading={isUploading}
              uploadProgress={uploadProgress}
            />
            <ImportInstructions />
          </div>
        );

      case 'preview':
        return (
          <ImportPreview
            previewData={previewData}
            fileName={selectedFile?.name}
            onConfirmImport={handleConfirmImport}
            onCancel={handleCancelPreview}
            isProcessing={isProcessing}
          />
        );

      case 'results':
        return (
          <ImportResults
            results={importResults}
            onStartNewImport={handleStartNewImport}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Cabeçalho da página */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground flex items-center">
                  <Icon name="Upload" size={28} className="mr-3" />
                  Importação de Membros
                </h1>
                <p className="text-muted-foreground mt-1">
                  Importe dados de filiados de arquivos CSV ou Excel com validação e prévia
                </p>
              </div>
              
              {/* Quick Stats */}
              <div className="hidden lg:flex items-center space-x-6 text-sm">
                <div className="text-center">
                  <div className="text-lg font-semibold text-foreground">CSV, XLS, XLSX</div>
                  <div className="text-muted-foreground">Formatos suportados</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-foreground">10MB</div>
                  <div className="text-muted-foreground">Tamanho máximo do arquivo</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-foreground">5</div>
                  <div className="text-muted-foreground">Campos obrigatórios</div>
                </div>
              </div>
            </div>
          </div>

          {/* Step Indicator */}
          {getStepIndicator()}

          {/* Main Content */}
          {renderCurrentStep()}
        </div>
      </main>

      {/* Overlay de processamento */}
      <LoadingOverlay
        isVisible={isProcessing}
        message="Processando dados dos membros..."
        type="processing"
        progress={null}
      />
    </div>
  );
};

export default MemberImport;